import React, { useState, useEffect, useCallback, useRef } from 'react';
import { jsPDF } from 'jspdf';
import api from '../../services/api';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Resolve a field's display value from cert data
function resolveFieldValue(f, cert) {
  const fv = cert.field_values || {};
  switch (f.field_type) {
    case 'full_name': return cert.member_name || fv.full_name || '';
    case 'membership_number': return cert.membership_number || fv.membership_number || '';
    case 'certificate_number': return cert.certificate_number || '';
    case 'issue_date': return cert.issue_date || '';
    case 'expiry_date': return cert.expiry_date || '';
    case 'title': return f.default_value || cert.title || '';
    case 'custom': return f.default_value || f.label || '';
    default: return fv[f.field_type] || fv[f.id] || f.label || '';
  }
}

// Render certificate on a canvas and return it
async function renderCertificateCanvas(cert) {
  const tpl = cert.template_data;
  if (!tpl) return null;
  const imgUrl = tpl.image_url || `${API_BASE}${tpl.image}`;
  const sigUrl = tpl.signature_image_url || null;
  const fields = tpl.fields_data || [];

  const bg = await new Promise((resolve, reject) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img); img.onerror = reject;
    img.src = imgUrl;
  });

  let sigImg = null;
  if (sigUrl) {
    sigImg = await new Promise((resolve) => {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img); img.onerror = () => resolve(null);
      img.src = sigUrl;
    });
  }

  // Ensure all Google Fonts are loaded before canvas drawing
  await document.fonts.ready;

  const canvas = document.createElement('canvas');
  canvas.width = bg.naturalWidth; canvas.height = bg.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bg, 0, 0);

  for (const f of fields) {
    const fx = (f.x / 100) * canvas.width;
    const fy = (f.y / 100) * canvas.height;
    const fw = (f.width / 100) * canvas.width;
    const fh = (f.height / 100) * canvas.height;

    if (f.field_type === 'signature' && sigImg) {
      const ratio = Math.min(fw / sigImg.naturalWidth, fh / sigImg.naturalHeight);
      const sw = sigImg.naturalWidth * ratio;
      const sh = sigImg.naturalHeight * ratio;
      ctx.drawImage(sigImg, fx + (fw - sw) / 2, fy + (fh - sh) / 2, sw, sh);
    } else if (f.field_type !== 'signature') {
      const scale = canvas.width / 800;
      const fontSize = Math.round(f.font_size * scale);
      const fontFam = f.font_family && f.font_family !== 'sans-serif' ? `'${f.font_family}', sans-serif` : 'sans-serif';
      ctx.font = `${f.font_weight === 'bold' ? 'bold ' : ''}${fontSize}px ${fontFam}`;
      ctx.fillStyle = f.font_color || '#000';
      ctx.textAlign = f.text_align || 'center';
      const tx = f.text_align === 'left' ? fx : f.text_align === 'right' ? fx + fw : fx + fw / 2;
      ctx.fillText(resolveFieldValue(f, cert), tx, fy + fh / 2 + fontSize / 3, fw);
    }
  }

  return canvas;
}

// Certificate preview thumbnail component — renders fields on the background
function CertPreview({ cert }) {
  const canvasRef = useRef();
  const [previewSrc, setPreviewSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    renderCertificateCanvas(cert)
      .then(canvas => {
        if (canvas && !cancelled) {
          setPreviewSrc(canvas.toDataURL('image/jpeg', 0.7));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cert]);

  const imgUrl = cert.template_data?.image_url;

  return (
    <div ref={canvasRef} className="relative h-40 bg-gray-50">
      <img
        src={previewSrc || imgUrl}
        alt={cert.title}
        className="w-full h-full object-contain"
      />
      <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">
        {cert.status_display || cert.status}
      </span>
    </div>
  );
}

// Generate & download certificate from template + field data
async function downloadCertificate(cert, format = 'png') {
  try {
    const canvas = await renderCertificateCanvas(cert);
    if (!canvas) { toast.error('No template data available'); return; }

    const certName = cert.certificate_number || 'certificate';

    if (format === 'pdf') {
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const orientation = canvas.width > canvas.height ? 'landscape' : 'portrait';
      const pdf = new jsPDF({ orientation, unit: 'px', format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${certName}.pdf`);
    } else if (format === 'jpeg') {
      const link = document.createElement('a');
      link.download = `${certName}.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      link.click();
    } else {
      const link = document.createElement('a');
      link.download = `${certName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
    toast.success('Certificate downloaded');
  } catch (err) {
    console.error(err);
    toast.error('Failed to generate certificate');
  }
}

export default function MemberCertificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCerts = useCallback(() => {
    setLoading(true);
    api.get('/my-certificates/')
      .then(res => setCerts(res.data.results || res.data))
      .catch(() => toast.error('Failed to load certificates'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">My Certificates</h1>
        <button onClick={fetchCerts} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#0B5D1E] border border-[#0B5D1E] rounded-lg hover:bg-[#0B5D1E]/5 transition disabled:opacity-50">
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading certificates...</p>
        </div>
      ) : certs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-500 mb-2">No Certificates</h2>
          <p className="text-gray-400">You don't have any issued certificates yet.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {certs.map(c => (
            <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition">
              {/* Certificate preview with rendered fields */}
              {c.template_data && <CertPreview cert={c} />}
              <div className="p-4 space-y-2">
                <h3 className="font-semibold text-gray-800 truncate">{c.title}</h3>
                <div className="text-xs text-gray-500 space-y-1">
                  <p><span className="font-medium text-gray-600">Cert #:</span> {c.certificate_number}</p>
                  <p><span className="font-medium text-gray-600">Issued:</span> {c.issue_date}</p>
                  {c.expiry_date && (
                    <p><span className="font-medium text-gray-600">Expires:</span> {c.expiry_date}</p>
                  )}
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => downloadCertificate(c, 'png')}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#0B5D1E] text-white rounded-lg text-xs font-semibold hover:bg-[#094a18] transition">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    PNG
                  </button>
                  <button onClick={() => downloadCertificate(c, 'jpeg')}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#C8A951] text-white rounded-lg text-xs font-semibold hover:bg-[#b89740] transition">
                    JPEG
                  </button>
                  <button onClick={() => downloadCertificate(c, 'pdf')}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#062E10] text-white rounded-lg text-xs font-semibold hover:bg-[#041f0b] transition">
                    PDF
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
